import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { SimpleResult } from "../../shared/interfaces/api";
import { GetProgramArgs, ProgramResponse } from "../interfaces/program";
import {
    CheckAuthResponse,
    LoginClientRequest,
    LoginClientResponse,
    RegisterClientRequest,
    RegisterClientResponse,
    LogoutResponse,
} from "../interfaces/auth";
import {
    ClientAddressRequest,
    ClientAddressResponse,
    ClientAddressUpdateRequest,
} from "../interfaces/client";
import {
    PaymentConfigRequest,
    PaymentConfigResponse,
    StripePaymentSetupRequest,
    StripePaymentSetupResponse,
} from "../interfaces/payment";
import { ReceiptRequest, ReceiptResponse }
    from "../interfaces/receipt";
import { ProgramClassResponse } from "../interfaces/programclass";
import {
    ProviderResponse,
    GetProviderArgs
} from "../interfaces/provider";
import {
    ProviderProgramsResponse
} from "../interfaces/providerprogram";

const TAG_AUTH = "Auth";
const TAG_CLIENT_ADDRESS = "ClientAddress"; // <-- Add a new tag type

export const registerApi = createApi({
    reducerPath: "registerApi",
    baseQuery: fetchBaseQuery({
        baseUrl: "/api/",
        prepareHeaders: (headers) => {
            return headers;
        },
    }),
    tagTypes: [TAG_AUTH, TAG_CLIENT_ADDRESS],
    endpoints: (builder) => ({
        getProvider: builder.query<ProviderResponse, GetProviderArgs>({
            query: ({ id }) => `register/provider/${id}`,
        }),
        getProviderPrograms: builder.query<ProviderProgramsResponse, GetProviderArgs>({
            query: ({ id }) => `register/providerprograms/${id}`,
        }),
        getProgram: builder.query<ProgramResponse, GetProgramArgs>({
            query: ({ id }) => `register/program/${id}`,
        }),
        getProgramClass: builder.query<ProgramClassResponse, GetProgramArgs>({
            query: ({ id }) => `register/programclass/${id}`,
        }),
        getClientAddress: builder.query<ClientAddressResponse, ClientAddressRequest>({
            query: (request) => ({
                url: `register/clientaddress`,
                headers: {
                    Authorization: `Bearer ${request.token}`,
                },
            }),
            providesTags: (result, error, arg) =>  // <-- Provide a tag for caching
                result ? [{ type: TAG_CLIENT_ADDRESS, id: "USER" }] : [], // Use a consistent ID
        }),
        getReceipt: builder.query<ReceiptResponse, ReceiptRequest>({
            query: ({ id }) => `register/receipt/${id}`,
        }),
        getPaymentConfig: builder.query<PaymentConfigResponse,PaymentConfigRequest>({
            query: ({ id }) => `register/paymentconfig/${id}`,
        }),
        getAuth: builder.query<CheckAuthResponse, void>({
            query: () => ({
                url: `account/check-auth`,
                credentials: "include",
            }),
            providesTags: [{ type: TAG_AUTH, id: "CHECK" }],
        }),
        stripePaymentSetup: builder.mutation<StripePaymentSetupResponse, StripePaymentSetupRequest>({
            query: (request) => ({
                url: "register/stripepaymentsetup",
                method: "POST",
                body: request,
            }),
        }),
        registerClient: builder.mutation<RegisterClientResponse, RegisterClientRequest>({
            query: (request) => ({
                url: "account/register-client",
                method: "POST",
                body: request,
                credentials: "include",
            }),
            invalidatesTags: [TAG_AUTH],
        }),
        loginClient: builder.mutation<LoginClientResponse, LoginClientRequest>({
            query: (request) => ({
                url: "account/login-client",
                method: "POST",
                body: request,
                credentials: "include",
            }),
            invalidatesTags: [TAG_AUTH],
        }),
        logout: builder.mutation<LogoutResponse, void>({ 
            query: () => ({
                url: "account/logout", // Adjust URL if needed
                method: "POST", // Use POST for actions with side effects, even without a body
                credentials: "include", // Send credentials (like session cookies)
            }),
            invalidatesTags: [TAG_AUTH], // Invalidate auth status on successful logout
        }),
        updateClientAddress: builder.mutation<ClientAddressResponse, ClientAddressUpdateRequest>({
            query: (request) => {
                const { token, ...addressData } = request; // Separate token from address data
                return {
                    url: `register/clientaddress`,
                    method: "PATCH",
                    body: addressData, // Send only address fields in the body
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                };
            },
            invalidatesTags: (result, error, arg) => // <-- Invalidate the client address cache
                [{ type: TAG_CLIENT_ADDRESS, id: "USER" }],
        }),
    // --- END NEW LOGOUT ENDPOINT ---
    }),
});

// export hooks
export const {
    useGetProgramQuery,
    useGetProviderProgramsQuery,
    useGetProgramClassQuery,
    useGetAuthQuery,
    useGetPaymentConfigQuery,
    useGetReceiptQuery,
    useGetClientAddressQuery,
    useStripePaymentSetupMutation,
    useRegisterClientMutation,
    useLoginClientMutation,
    useLogoutMutation,
    useGetProviderQuery, 
    useUpdateClientAddressMutation,
} = registerApi;
